// Kjell Knallhard logo for Fjes og fjas: a still of his neutral smirk that every 3–6 s plays a short antic
// (trutmunn, raspberry, ...) and lands back on the same still. Markup comes from snippets/kjell-logo.liquid.
// Each antic is a WebP sprite sheet with alpha drawn frame by frame on a canvas: Shopify keeps video out of theme
// assets. The first antic plays once the page has loaded, only the next sheet is fetched ahead, and nothing moves
// for reduced motion. While the tab is hidden, the favicon plays antics instead (see FAVICON).

export function nextDelay(minS, maxS, r = Math.random()) {
  return (minS + r * (maxS - minS)) * 1000;
}

// A random antic, never the one just played.
export function pickClip(names, last, r = Math.random()) {
  const pool = names.length > 1 ? names.filter((n) => n !== last) : names;
  return pool[Math.min(pool.length - 1, Math.floor(r * pool.length))];
}

// The source rectangle of frame `f` in a sheet with `cols` columns of `size` px frames.
export function frameRect(f, cols, size) {
  return { x: (f % cols) * size, y: Math.floor(f / cols) * size };
}

// Favicon antics while the tab is hidden (Ole 14.10). The favicon is mostly seen in a background tab, where Chrome
// runs timers at most once a second and, after 5 minutes hidden, once a minute. So Kjell shows three key frames of an
// antic at 1 fps every 20–30 s and stops before the 5 minutes. Only antics that read at 32 px: puff and eyebrows do
// not. Safari keeps the static icon. Only links marked data-kjell-favicon change, so a favicon set in theme settings
// stays put.
export const FAVICON = {
  clips: ["trutmunn", "raspberry", "headbob", "crosseyed"],
  frames: [10, 14, 18], // the peak of every clip at 12 fps
  px: 32,
  first: 5,
  min: 20,
  max: 30,
  stopAfter: 240,
  disc: "#e8732a", // as kjell-favicon-32.png
};

// The favicon's square (eyes to chin plus hair) in a frame of `size` px: logo.ts favicon crops 420,300,760 px from the
// 1600x2000 cut-out, and the frames are the square 175,50,1250 px of the same image.
export function faviconCrop(size) {
  return { x: Math.round((size * 245) / 1250), y: Math.round((size * 250) / 1250), size: Math.round((size * 760) / 1250) };
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Defined only in the browser, so the helpers above import in tests.
const Base = typeof HTMLElement === "undefined" ? class {} : HTMLElement;

class KjellLogo extends Base {
  connectedCallback() {
    this.face = this.querySelector("[data-kjell-face]");
    this.sheets = JSON.parse(this.dataset.sheets || "{}");
    const d = this.dataset;
    this.spec = { frames: Number(d.frames), cols: Number(d.cols), size: Number(d.size), fps: Number(d.fps) };
    this.min = Number(d.min || 3); // was 5–10 s; Ole wanted antics more often (13.10)
    this.max = Number(d.max || 6);
    if (!this.face || !Object.keys(this.sheets).length || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const start = () => this.start();
    if (document.readyState === "complete") start();
    else addEventListener("load", start, { once: true });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    if (this.onResize) removeEventListener("resize", this.onResize);
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility);
    this.stopFavicon();
  }

  start() {
    if (!this.isConnected || this.canvas) return;
    this.canvas = document.createElement("canvas");
    this.canvas.className = "kjell-logo__clip";
    this.canvas.setAttribute("aria-hidden", "true");
    this.face.append(this.canvas);
    this.ctx = this.canvas.getContext("2d");
    this.fitCanvas();
    this.onResize = () => this.fitCanvas();
    addEventListener("resize", this.onResize);
    this.loaded = new Map();
    this.visible = true;
    this.observer = new IntersectionObserver(([e]) => {
      this.visible = e.isIntersecting;
      this.schedule();
    });
    this.observer.observe(this);
    this.onVisibility = () => {
      if (document.hidden) this.startFavicon();
      else this.stopFavicon();
      this.schedule();
    };
    document.addEventListener("visibilitychange", this.onVisibility);
    this.addEventListener("pointerenter", () => this.play());
    // The first antic plays as soon as its sheet is in (Ole 13.10); later ones follow the 3–6 s rhythm. A tab opened
    // in the background starts with the favicon.
    if (document.hidden) this.onVisibility();
    else this.play();
  }

  // The canvas matches the face's device pixels, so the browser never rescales it (that blurred the frames); the
  // frame is scaled once into it with high-quality smoothing.
  fitCanvas() {
    const px = Math.max(1, Math.round(this.face.getBoundingClientRect().width * (window.devicePixelRatio || 1)));
    if (this.canvas.width === px) return;
    this.canvas.width = this.canvas.height = px;
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
  }

  // Picks the next antic now and fetches only its sheet, so a visit costs one sheet per antic shown.
  prepare() {
    if (!this.next) this.next = pickClip(Object.keys(this.sheets), this.last);
    return this.sheet(this.next);
  }

  sheet(name) {
    if (!this.loaded.has(name)) {
      const img = new Image();
      img.decoding = "async";
      img.src = this.sheets[name];
      this.loaded.set(name, img.decode().then(() => img));
    }
    return this.loaded.get(name);
  }

  startFavicon() {
    this.icons ??= [...document.querySelectorAll("link[rel~='icon'][data-kjell-favicon]")].map((l) => [l, l.href]);
    if (!this.icons.length) return;
    this.stopFavicon();
    this.favUntil = performance.now() + FAVICON.stopAfter * 1000;
    this.nextFavicon(this.favRun, FAVICON.first * 1000);
  }

  // Ends the current round (its pending steps see a new favRun) and puts the static icon back.
  stopFavicon() {
    this.favRun = (this.favRun || 0) + 1;
    clearTimeout(this.favTimer);
    this.setIcon(null);
  }

  nextFavicon(run, ms) {
    if (performance.now() + ms < this.favUntil) this.favTimer = setTimeout(() => this.faviconAntic(run), ms);
  }

  async faviconAntic(run) {
    const name = pickClip(FAVICON.clips.filter((n) => this.sheets[n]), this.favLast);
    this.favLast = name;
    let img;
    try {
      img = await this.sheet(name);
    } catch {
      this.loaded.delete(name);
    }
    for (const f of img ? FAVICON.frames : []) {
      if (run !== this.favRun) return;
      this.setIcon(this.faviconFrame(img, f));
      await wait(1000);
    }
    if (run !== this.favRun) return;
    this.setIcon(null);
    this.nextFavicon(run, nextDelay(FAVICON.min, FAVICON.max));
  }

  faviconFrame(img, f) {
    const { px, disc } = FAVICON;
    const { cols, size } = this.spec;
    this.favCanvas ??= Object.assign(document.createElement("canvas"), { width: px, height: px });
    const ctx = this.favCanvas.getContext("2d");
    const { x, y } = frameRect(f, cols, size);
    const c = faviconCrop(size);
    ctx.clearRect(0, 0, px, px);
    ctx.save();
    ctx.beginPath();
    ctx.arc(px / 2, px / 2, px / 2, 0, 2 * Math.PI);
    ctx.clip();
    ctx.fillStyle = disc;
    ctx.fillRect(0, 0, px, px);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, x + c.x, y + c.y, c.size, c.size, 0, 0, px, px);
    ctx.restore();
    return this.favCanvas.toDataURL("image/png");
  }

  // null puts the static icons back; only touches the links when something changes, so they are not refetched.
  setIcon(url) {
    if (!url && !this.favShown) return;
    this.favShown = Boolean(url);
    for (const [link, href] of this.icons ?? []) link.href = url ?? href;
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this.canvas || this.playing || !this.visible || document.hidden) return;
    this.prepare().catch(() => {});
    this.timer = setTimeout(() => this.play(), nextDelay(this.min, this.max));
  }

  async play() {
    if (!this.canvas || this.playing) return;
    clearTimeout(this.timer);
    this.playing = true;
    const name = this.next ?? pickClip(Object.keys(this.sheets), this.last);
    this.next = name;
    let img;
    try {
      img = await this.prepare();
    } catch {
      this.loaded.delete(name);
      this.next = null;
      this.playing = false;
      return this.schedule();
    }
    this.last = name;
    this.next = null;
    const { frames, cols, size, fps } = this.spec;
    const t0 = performance.now();
    const step = (now) => {
      const f = Math.floor(((now - t0) * fps) / 1000);
      if (f >= frames) {
        this.face.classList.remove("is-playing");
        this.playing = false;
        return this.schedule();
      }
      const { x, y } = frameRect(f, cols, size);
      const px = this.canvas.width;
      this.ctx.clearRect(0, 0, px, px);
      this.ctx.drawImage(img, x, y, size, size, 0, 0, px, px);
      this.face.classList.add("is-playing");
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }
}

if (typeof customElements !== "undefined" && !customElements.get("kjell-logo")) customElements.define("kjell-logo", KjellLogo);
